<template>
  <v-dialog v-model="visible" scrollable persistent max-width="900" @keydown.esc="cancelForm()">
    <v-card class="movement-form-card">
      <!-- Encabezado mejorado -->
      <v-card-title class="primary white--text py-4">
        <v-icon left color="white">{{ isNewRecord ? 'mdi-truck-fast' : 'mdi-truck-check' }}</v-icon>
        {{ title }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cancelForm()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <!-- Contenido principal -->
      <v-card-text class="pa-0">
        <v-form ref="movimientoForm" v-model="validations.isValid">
          <!-- Información del vehículo -->
          <div v-if="selectedVehicle" class="vehicle-info-header pa-4">
            <v-row align="center">
              <v-col cols="auto">
                <v-avatar size="64" color="primary" class="white--text">
                  <v-icon size="36">mdi-car</v-icon>
                </v-avatar>
              </v-col>
              <v-col>
                <div class="text-h5 font-weight-medium">{{ selectedVehicle.brand }} {{ selectedVehicle.model }}</div>
                <div class="subtitle-1">{{ selectedVehicle.plate_number }} · {{ selectedVehicle.color }}</div>
                <v-chip small class="mt-1" color="primary" outlined>VIN: {{ selectedVehicle.frame_number }}</v-chip>
              </v-col>
              <v-col cols="auto" v-if="tieneMovimientos">
                <v-chip color="success" class="text-caption">
                  <v-icon left small>mdi-car-connected</v-icon>
                  Registrado en sistema
                </v-chip>
              </v-col>
              <v-col cols="auto" v-else>
                <v-chip color="warning" class="text-caption">
                  <v-icon left small>mdi-car-arrow-right</v-icon>
                  Primer movimiento
                </v-chip>
              </v-col>
            </v-row>
          </div>
          <v-divider></v-divider>
          <v-container fluid class="pa-6">
            <!-- Contenedor de pestañas para separar el contenido -->
            <v-tabs v-model="activeTab" background-color="transparent" color="primary" grow slider-size="3">
              <v-tab v-if="value.type === 'entry'">
                <v-icon left>mdi-clipboard-check-outline</v-icon>
                Recepción Inicial
              </v-tab>
              <v-tab v-else-if="value.type === 'move'">
                <v-icon left>mdi-swap-horizontal</v-icon>
                Movimiento
              </v-tab>
              <v-tab>
                <v-icon left>mdi-car-info</v-icon>
                Estado del Vehículo
              </v-tab>
            </v-tabs>
            <v-divider></v-divider>
            <v-tabs-items v-model="activeTab" class="pt-4">
              <!-- Pestaña de recepción inicial -->
              <v-tab-item v-if="!tieneMovimientos">
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select v-model="fields.transportation" :items="modosTransporteChecking"
                          label="Modo de Transporte" :rules="validations.transportation"
                          :error-messages="errors.transportation" prepend-inner-icon="mdi-truck-delivery" outlined dense
                          required>
                          <template v-slot:selection="{ item }">
                            <v-icon left small :color="getTransportColor(item)">{{ getTransportIcon(item) }}</v-icon>
                            {{ item }}
                          </template>
                          <template v-slot:item="{ item }">
                            <v-icon left :color="getTransportColor(item)">{{ getTransportIcon(item) }}</v-icon>
                            {{ item }}
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="12" sm="6" v-if="fields.transportation === 'GRUA'">
                        <v-text-field v-model="fields.transport_company" label="Nombre de la empresa"
                          :rules="validations.transport_company" :error-messages="errors.transport_company"
                          prepend-inner-icon="mdi-domain" outlined dense required></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select v-model="fields.source_location_id" :items="ubicaciones"
                          label="Ubicación de Recepción" :rules="validations.reception_location_id"
                          :error-messages="errors.reception_location_id" prepend-inner-icon="mdi-map-marker" outlined
                          dense required></v-select>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="fields.initial_km" label="KM Iniciales" :rules="validations.initial_km"
                          :error-messages="errors.initial_km" prepend-inner-icon="mdi-speedometer" type="number"
                          outlined dense required></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select 
                          v-model="driverType"
                          :items="vehicleTypeOptions" 
                          item-text="text"
                          item-value="value"
                          label="Estado de matrícula"
                          :rules="validations.registered" 
                          :error-messages="errors.registered"
                          :error="!driverType || driverType === ''"
                          prepend-inner-icon="mdi-card-account-details" 
                          outlined 
                          dense 
                          required
                          hint="Campo obligatorio"
                          persistent-hint
                          :disabled="isMovementCompleted">
                        </v-select>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>
              <!-- Pestaña de movimientos -->
              <v-tab-item v-else>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <custom-date-time-picker 
                          :value="fields.datetime" 
                          label="Fecha y Hora" 
                          :rules="validations.datetime"
                          :error-messages="errors.datetime" 
                          prepend-inner-icon="mdi-calendar-clock"
                          dense
                          @input="updateDateValue($event)"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select v-model="fields.source_location_id" :items="ubicaciones" label="Ubicación de Origen"
                          :rules="validations.source_location_id" :error-messages="errors.source_location_id"
                          prepend-inner-icon="mdi-map-marker-left" outlined dense required></v-select>
                      </v-col>
                      <v-col cols="12" sm="6" v-if="value.type !== 'entry'">
                        <v-select v-model="fields.destination_location_id" :items="ubicaciones"
                          label="Ubicación de Destino" :rules="validations.destination_location_id"
                          :error-messages="errors.destination_location_id" prepend-inner-icon="mdi-map-marker-right"
                          outlined dense required></v-select>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select v-model="fields.transportation" :items="modosTransporte" label="Modo de Transporte"
                          :rules="validations.transportation" :error-messages="errors.transportation"
                          prepend-inner-icon="mdi-truck-delivery" outlined dense required>
                          <template v-slot:selection="{ item }">
                            <v-icon left small :color="getTransportColor(item)">{{ getTransportIcon(item) }}</v-icon>
                            {{ item }}
                          </template>
                          <template v-slot:item="{ item }">
                            <v-icon left :color="getTransportColor(item)">{{ getTransportIcon(item) }}</v-icon>
                            {{ item }}
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="12" sm="6"
                        v-if="fields.transportation === 'DRIVER' || fields.transportation === 'CLIENTE'">
                        <v-autocomplete v-model="selectedDriver" :items="driverState.list"
                          :loading="driverState.loading" :search-input.sync="driverState.search" :disabled="loading"
                          :filter="filterDrivers" :item-text="displayText" item-value="dni"
                          label="¿Quién es el conductor?" prepend-inner-icon="mdi-account-tie" hide-details="auto"
                          outlined dense required hide-no-data hide-selected return-object>
                          <template v-slot:item="data">
                            <v-list-item-avatar>
                              <v-icon color="primary">mdi-account-circle</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title v-html="`${data.item.name} ${data.item.lastname}`"></v-list-item-title>
                              <v-list-item-subtitle v-html="`DNI: ${data.item.document_number}`"></v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-select 
                          v-model="driverType"
                          :items="vehicleTypeOptions" 
                          item-text="text"
                          item-value="value"
                          label="Estado de matrícula"
                          :rules="validations.registered" 
                          :error-messages="errors.registered"
                          :error="!driverType || driverType === ''"
                          prepend-inner-icon="mdi-card-account-details" 
                          outlined 
                          dense 
                          required
                          hint="Campo obligatorio"
                          persistent-hint
                          :disabled="isMovementCompleted">
                        </v-select>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="selectedUser" :rules="validations.responsible"
                          :error-messages="errors.responsible" :items="userState.list" :loading="userState.loading"
                          :search-input.sync="userState.search" :disabled="loading" :filter="filterUsers"
                          :item-text="displayTextUser" item-value="id" label="Usuario Responsable"
                          prepend-inner-icon="mdi-account-key" hide-details="auto" return-object required outlined
                          dense>
                          <template v-slot:item="data">
                            <v-list-item-avatar>
                              <v-icon color="primary">mdi-account-circle</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title v-html="`${data.item.name} ${data.item.lastname}`"></v-list-item-title>
                              <v-list-item-subtitle v-html="`Email: ${data.item.email}`"></v-list-item-subtitle>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>
              <!-- Pestaña de estado del vehículo -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-radio-group v-model="fields.vehicle_status" :rules="validations.vehicle_status"
                          :error-messages="errors.vehicle_status" label="Estado del Vehículo" required>
                          <v-radio label="Ok" value="good" color="success">
                            <template v-slot:label>
                              <v-icon left small color="success">mdi-check-circle</v-icon>
                              Ok
                            </template>
                          </v-radio>
                          <v-radio label="Hay daños" value="damages" color="error">
                            <template v-slot:label>
                              <v-icon left small color="error">mdi-alert-circle</v-icon>
                              Hay daños
                            </template>
                          </v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                    <v-expand-transition>
                      <div v-if="fields.vehicle_status === 'damages'">
                        <v-row>
                          <v-col cols="12">
                            <v-textarea v-model="fields.damage_notes" label="Descripción de los daños"
                              :rules="validations.damage_notes" :error-messages="errors.damage_notes"
                              prepend-inner-icon="mdi-note-text" outlined dense auto-grow rows="3" counter
                              required></v-textarea>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <div class="images-container mb-4">
                              <v-card outlined>
                                <v-card-text class="pa-2">
                                  <div class="d-flex align-center mb-2">
                                    <v-icon small color="primary" class="mr-2">mdi-image</v-icon>
                                    <span class="subtitle-2">Imágenes del vehículo</span>
                                    <v-spacer></v-spacer>
                                    <v-chip small color="primary" class="ml-auto" outlined>
                                      {{ totalImagesCount }}/4
                                    </v-chip>
                                  </div>

                                  <!-- Selector de nuevas imágenes -->
                                  <v-file-input v-if="!isMovementCompleted && canAddMoreImages"
                                    v-model="fields.damage_images" label="Añadir imágenes" prepend-icon="mdi-camera"
                                    accept="image/*" multiple chips show-size :counter="remainingImageSlots" outlined
                                    @change="handleImageChange" hint="Haga clic o arrastre para añadir imágenes"
                                    persistent-hint class="mb-4"></v-file-input>

                                  <!-- Todas las imágenes (guardadas + nuevas) -->
                                  <v-row dense>
                                    <!-- Imágenes guardadas -->
                                    <v-col v-for="(image, index) in fields.imagesUrl" :key="'saved-' + index" cols="6"
                                      sm="4" md="3">
                                      <v-hover v-slot="{ hover }">
                                        <v-card class="mx-auto image-preview-card" :elevation="hover ? 4 : 1">
                                          <v-img :src="image.url || image" aspect-ratio="1" class="image-preview" cover>
                                            <template v-slot:placeholder>
                                              <v-row class="fill-height ma-0" align="center" justify="center">
                                                <v-progress-circular indeterminate
                                                  color="primary"></v-progress-circular>
                                              </v-row>
                                            </template>
                                            <div class="image-overlay d-flex align-center justify-center" v-if="hover">
                                              <v-btn small icon @click.stop="openImageDialog(image.url || image)"
                                                class="mx-1">
                                                <v-icon small color="white">mdi-eye</v-icon>
                                              </v-btn>
                                              <v-btn small icon @click.stop="removeSavedImage(index)" color="error"
                                                class="mx-1">
                                                <v-icon small>mdi-delete</v-icon>
                                              </v-btn>
                                            </div>
                                          </v-img>
                                        </v-card>
                                      </v-hover>
                                    </v-col>

                                    <!-- Previews de nuevas imágenes -->
                                    <v-col v-for="(preview, index) in imagePreviewUrls" :key="'new-' + index" cols="6"
                                      sm="4" md="3">
                                      <v-hover v-slot="{ hover }">
                                        <v-card class="mx-auto new-image-preview-card" :elevation="hover ? 4 : 1">
                                          <v-img :src="preview" aspect-ratio="1" class="image-preview" cover>
                                            <div class="image-overlay d-flex align-center justify-center" v-if="hover">
                                              <v-btn small icon @click.stop="openImageDialog(preview)" class="mx-1">
                                                <v-icon small color="white">mdi-eye</v-icon>
                                              </v-btn>
                                              <v-btn small icon @click.stop="removeNewImage(index)" color="error"
                                                class="mx-1">
                                                <v-icon small>mdi-delete</v-icon>
                                              </v-btn>
                                            </div>
                                            <div class="image-badge">
                                              <v-chip x-small label color="primary" dark>Nueva</v-chip>
                                            </div>
                                          </v-img>
                                        </v-card>
                                      </v-hover>
                                    </v-col>
                                  </v-row>

                                  <!-- Mensaje cuando no se pueden añadir más imágenes -->
                                  <v-alert v-if="!canAddMoreImages && !isMovementCompleted" type="warning" dense text
                                    class="mt-2">
                                    Ha alcanzado el límite máximo de 4 imágenes. Elimine alguna imagen existente para
                                    añadir una nueva.
                                  </v-alert>
                                </v-card-text>
                              </v-card>
                            </div>
                          </v-col>
                        </v-row>

                      </div>
                    </v-expand-transition>
                    <v-row>
                      <v-col cols="12">
                        <div class="section-title">
                          <v-icon color="primary" left>mdi-car-emergency</v-icon>
                          <span>Equipamiento básico</span>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-card outlined class="pa-2 equipment-card">
                          <v-checkbox v-model="fields.emergency_vest" label="Chalecos reflectantes" :true-value="1"
                            :false-value="0" color="green" prepend-icon="mdi-shield-account" hide-details
                            class="mt-0"></v-checkbox>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-card outlined class="pa-2 equipment-card">
                          <v-checkbox v-model="fields.emergency_light" label="Luz de emergencia" :true-value="1"
                            :false-value="0" color="orange" prepend-icon="mdi-led-on" hide-details
                            class="mt-0"></v-checkbox>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-card outlined class="pa-2 equipment-card">
                          <v-checkbox v-model="fields.emergency_triangle" label="Triángulos reflectantes"
                            :true-value="1" :false-value="0" color="red" prepend-icon="mdi-triangle" hide-details
                            class="mt-0"></v-checkbox>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-container>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <!-- Acciones mejoradas -->
      <v-card-actions class="pa-4">
        <v-alert v-if="showAlert" :type="alertType" dense text class="ma-0 mr-4">
          {{ alertMessage }}
        </v-alert>
        <v-spacer></v-spacer>
        <v-btn :disabled="loading" @click="cancelForm()" color="grey darken-1" text class="px-4">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn :loading="loading" :disabled="saveIsDisabled" @click="saveForm()" color="primary" class="px-4" depressed>
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CustomDateTimePicker from '../../components/CustomDateTimePicker.vue'
import VehiculoService from '@/services/VehiculoService'
import LocationService from '@/services/LocationService'
import { locationDisplayName } from '@/utils'
import UserService from '@/services/UserService'
import DriverService from '@/services/DriverService'
import MovimientoService from '@/services/MovimientoService'
import _ from 'lodash'
import { mapGetters } from 'vuex'

export default {
  components: {
    CustomDateTimePicker
  },
  props: {
    value: null
  },

  data: () => ({
    visible: true,
    loading: false,
    movimiento: null,
    tieneMovimientos: false,
    activeTab: 0,    
    showAlert: false,
    alertType: 'info',
    alertMessage: '',
    imageDialog: false,
    selectedImageUrl: null,
    imagesToRemove: [],
    imagePreviewUrls: [],
    driverType: '',

    // campos del formulario
    fields: {
      vehicle_id: null,
      type: 'entry',
      datetime: null,
      delivery_date: null,
      source_location_id: null,
      destination_location_id: null,
      transportation: null,
      responsible_id: null,
      responsible: null,
      status: 'unreported',
      observations: null,
      driver_id: null,
      initial_km: null,
      vehicle_status: null,
      registered: null,
      _images: [],
      transport_company: null,
      reception_location_id: null,
      damage_notes: null,
      damage_images: [],
      emergency_triangle: 0,
      emergency_light: 0,
      emergency_vests: 0
    },

    // búsqueda de vehículo
    vehiculoState: {
      list: [],
      loading: false,
      search: ''
    },
    selectedVehicle: null,

    // búsqueda de usuario
    userState: {
      list: [],
      loading: false,
      search: ''
    },
    selectedUser: null,

    driverState: {
      list: [],
      loading: false,
      search: ''
    },
    selectedDriver: null,

    // lista de ubicaciones y modos de transporte
    ubicaciones: [],
    modosTransporte: ['DRIVER', 'GRUA', 'YO MISMO', 'CLIENTE'],
    modosTransporteChecking: ['GRUA', 'CLIENTE'],

    // validaciones
    validations: {
      isValid: true,
      vehicle_id: [
        (v) => !!v || 'Debe proporcionar un ID de vehículo.'
      ],
      driver_id: [],
      datetime: [
        (v) => !!v || 'Debe proporcionar una fecha y hora.'
      ],
      source_location_id: [
        (v) => !!v || 'Debe proporcionar una ubicación de origen.'
      ],
      destination_location_id: [
        (v) => !!v || 'Debe proporcionar una ubicación de destino.'
      ],
      transportation: [
        (v) => !!v || 'Debe proporcionar un modo de transporte.'
      ],
      responsible: [
        (v) => !!v || 'Debe proporcionar un responsable.'
      ],
      status: [
        (v) => !!v || 'Debe proporcionar un estado.'
      ],
      observations: [],
      initial_km: [
        (v) => !!v || 'Debe proporcionar el kilometraje inicial.'
      ],      
      vehicle_status: [
        (v) => !!v || 'Debe proporcionar el estado del vehículo.'
      ],
      registered: [
        (v) => !!v || 'Debe proporcionar si está matriculado.'
      ],
      _images: [
        (v) => v.length <= 5 || 'No puede subir más de 5 imágenes.'
      ],
      damage_notes: [
        (v, fields) => !fields || !fields.vehicle_status || fields.vehicle_status !== 'Hay daños' || !!v || 'Debe describir los daños.'
      ]
    },

    // errores
    errors: {
      vehicle_id: [],
      driver_id: [],
      datetime: [],
      source_location_id: [],
      destination_location_id: [],
      transportation: [],
      responsible: [],
      status: [],
      observations: [],
      initial_km: [],
      vehicle_status: [],
      registered: [],
      _images: [],
      damage_notes: [],
      damage_images: []
    }
  }),

  async created () {
    this.selectedVehicle = this.vehicle
    VehiculoService.get().then(({ data }) => {
      this.vehiculoState.list = data.data
    })
    /* UserService.get().then(({ data }) => {
      this.userState.list = data.data
    }) */
    if (this.value) {
    if (this.value.driver_id) {
      DriverService.get({ 'filter[id]': this.value.driver_id }).then(({ data }) => {
        this.driverState.list = data.data
        this.selectedDriver = this.driverState.list[0]      
      })
    }
    this.fields = Object.assign({}, this.value)
    const preferredDate = this.fields.delivery_date || this.fields.datetime
    this.fields.delivery_date = preferredDate
    this.fields.datetime = preferredDate
    
    this.selectedVehicle = this.fields.vehicle
    if (this.fields.registered === 1) {
      this.driverType = 'MATRICULADO'
    } else if (this.fields.registered === 0) {
      this.driverType = 'PLACAS ROJAS'
    } else if (this.fields.registered === 2) {
      this.driverType = 'NO MATRICULADO'
    } else {
      this.driverType = ''
    }

    if (this.fields.responsible_id) {
      this.userState.list[0] = this.fields.responsible
      this.selectedUser = this.userState.list[0]
        /* const userExists = this.userState.list.some(user => user.id === this.selectedUser.id)
        if (!userExists) {
          this.userState.list.push(this.selectedUser)
        } */
      }
      // El CustomDateTimePicker maneja timestamps directamente, no necesitamos convertir
      // if (this.fields.datetime) {
      //   this.fields.datetime = new Date(this.fields.datetime * 1000).toISOString().substr(0, 16)
      // }
      this.fields.imagesUrl = Array.isArray(this.value.imagesUrls) ? this.value.imagesUrls : []
      // Si no es array pero hay imágenes, crea un array
      if (!Array.isArray(this.fields.imagesUrl) && this.value.imagesUrls) {
        this.fields.imagesUrl = [this.value.imagesUrls]
      }
      // Si hay observaciones, usarlas como damage_notes
      if (this.fields.observations) {
        this.fields.damage_notes = this.fields.observations
      }
    } else {
    // Si es un registro nuevo, inicializamos el array de imágenes
      this.fields.imagesUrl = []
    }
    this.loading = false

    // Cargar ubicaciones desde LocationService
    try {
      // N-17: pasamos la condición del vehículo para ocultar colaboradores que no trabajan con esa condición.
      // vehicle_id: para que el backend restrinja al propietario del vehículo + sus partners (en lugar
      // de incluir otros dealers con los que el colaborador también trabaja).
      const params = { 'per-page': 1000, expand: 'dealer,dealers' }
      const vehicleCondition = this.selectedVehicle && this.selectedVehicle.condition ? String(this.selectedVehicle.condition).toLowerCase() : null
      if (vehicleCondition === 'new' || vehicleCondition === 'used') {
        params['filter[vehicle_condition]'] = vehicleCondition
      }
      if (this.selectedVehicle && this.selectedVehicle.id) {
        params['filter[vehicle_id]'] = this.selectedVehicle.id
      }
      const { data } = await LocationService.get(params)
      const ownDealer = this.$store.getters.CURRENT_USER && this.$store.getters.CURRENT_USER.dealer
      const preferredDealer = (this.selectedVehicle && this.selectedVehicle.cedido_meta && this.selectedVehicle.cedido_meta.holder)
        || (this.selectedVehicle && this.selectedVehicle.dealer)
        || null
      this.ubicaciones = data.data.map(location => ({ text: locationDisplayName(location, ownDealer, preferredDealer), value: location.id }))
    } catch (error) {
      console.error('Error al cargar ubicaciones:', error)
    }
    // Verificar si el vehículo tiene movimientos
    if (this.selectedVehicle) {
      this.tieneMovimientos = await this.tieneMovimiento()
    }
  },

  methods: {
    async saveForm () {
      this.$refs.movimientoForm.validate()
      if (this.validations.isValid) {
        this.loading = true

        // Asigna solo el ID del vehículo seleccionado
        this.fields.vehicle_id = this.selectedVehicle ? this.selectedVehicle.id : null

        // Asigna solo el ID del driver seleccionado
        this.fields.driver_id = this.selectedDriver ? this.selectedDriver.id : null

        // Asigna solo el ID del usuario seleccionado
        this.fields.responsible_id = this.selectedUser ? this.selectedUser.id : this.currentUser.id
        this.fields.driver_id = this.selectedDriver ? this.selectedDriver.id : null

        const preferredDate = this.fields.delivery_date ?? this.fields.datetime
        this.fields.delivery_date = preferredDate
        this.fields.datetime = preferredDate

        if (!this.fields.datetime) {
          const now = Math.floor(Date.now() / 1000)
          this.fields.datetime = now
          this.fields.delivery_date = now
        }
        
        this.fields.destination_location_id = !this.fields.destination_location_id ? this.fields.source_location_id : this.fields.destination_location_id
        // this.fields.vehicle_status = this.fields.vehicle_status ? this.fields.vehicle_status === 'Ok' ? 'good' : 'damages' : null
        this.fields.basic_equipment = this.fields.basic_equipment ? this.fields.basic_equipment === 'Si' : null
        // Validación obligatoria de estado de matrícula - misma lógica que MovementProcessingView.vue:2272 y canComplete:1990
        if (!this.driverType || this.driverType === '') {
          this.$toast.error('Debe seleccionar el estado de matrícula para completar el movimiento')
          this.showAlert = true
          this.alertType = 'error'
          this.alertMessage = 'Debe seleccionar el estado de matrícula.'
          this.loading = false
          return
        }
        // Mapping MATRICULADO -> 1, NO MATRICULADO -> 2, PLACAS ROJAS -> 0 - misma lógica que TransportForm.vue:1575 / MovementProcessing
        this.fields.registered = this.driverType === 'MATRICULADO' ? 1 : this.driverType === 'NO MATRICULADO' ? 2 : this.driverType === 'PLACAS ROJAS' ? 0 : null

        if (!this.tieneMovimientos) {
          await VehiculoService.put({ id: this.fields.vehicle_id, location_id: this.fields.source_location_id }).then(({ data }) => {
          })
        }

        // Ya no necesitamos convertir la fecha porque el CustomDateTimePicker ya devuelve timestamp
        // if (this.fields.datetime) {
        //   this.fields.datetime = Math.floor(new Date(this.fields.datetime).getTime() / 1000)
        // }

        const finalDate = this.fields.delivery_date ?? this.fields.datetime
        this.fields.datetime = finalDate
        this.fields.delivery_date = finalDate

        let movimiento = Object.assign({}, this.fields, {
          datetime: finalDate,
          delivery_date: finalDate
        })
        try {
          const response = await this.$store.dispatch(this.isNewRecord ? 'ADD_MOVIMIENTO' : 'UPDATE_MOVIMIENTO', movimiento)
          const { error, data, status } = response

          if (error) {
            if (status === 422) {
              // Mostrando los errores de validación en los campos correspondientes
              data.data.forEach(({ field, message }) => {
                if (this.errors[field]) {
                  this.errors[field].push(message)
                }
              })
              this.showAlert = true
              this.alertType = 'error'
              this.alertMessage = 'Hay errores de validación. Por favor revise el formulario.'
            } else {
              // Mostrando cualquier otro error al usuario en una notificación
              this.$toast.error('Ocurrió un error al intentar registrar o actualizar el movimiento')
              this.showAlert = true
              this.alertType = 'error'
              this.alertMessage = 'Ocurrió un error. Inténtelo de nuevo.'
            }          
          } else {
            // Enviar las imágenes una por una
            if (this.fields.damage_images && this.fields.damage_images.length > 0) {
              for (let index = 0; index < this.fields.damage_images.length; index++) {
                const image = this.fields.damage_images[index]
                const formData = new FormData()
                formData.append('_image', image) // Agrega la imagen al FormData

                try {
                  await MovimientoService.addImages(data.data.id, formData)
                } catch (error) {
                  console.error('Error al enviar la imagen:', error)
                }
              }
            }

            // Emitir el evento de guardado y redirigir
            this.$emit('save', { insert: this.isNewRecord, value: data })
            this.$toast.success('Movimiento guardado exitosamente')
            this.loading = false
            // this.$router.push({ name: 'Movimiento' })
          }
        } catch (error) {
          console.error('Error al guardar el movimiento:', error)
          this.$toast.error('Ocurrió un error al intentar registrar o actualizar el movimiento')
          this.showAlert = true
          this.alertType = 'error'
          this.alertMessage = 'Error de conexión. Inténtelo de nuevo.'
          this.loading = false
        }
      }
    },
    updateDateValue (value) {
      this.fields.datetime = value
      this.fields.delivery_date = value
    },
    cancelForm () {
      this.$emit('cancel')
    },
    vehiculoDebouncedSearch: _.debounce(function () {
      this.vehiculoState.loading = true
      VehiculoService.get({ 'filter[search]': this.vehiculoState.search }).then(({ data }) => {
        this.vehiculoState.list = data.data
        this.vehiculoState.loading = false
      })
    }, 1000),
    driverDebouncedSearch: _.debounce(function () {
      this.driverState.loading = true
      DriverService.get({ 'filter[search]': this.driverState.search }).then(({ data }) => {
        this.driverState.list = data.data
        this.driverState.loading = false
      })
    }, 1000),
    userDebouncedSearch: _.debounce(function () {
      this.userState.loading = true
      UserService.get({ 'filter[search]': this.userState.search }).then(({ data }) => {
        this.userState.list = data.data
        this.userState.loading = false
      })
    }, 1000),
    filterVehiculos (item, queryText, itemText) {
      const text = `${item.brand} ${item.model} ${item.plate_number} ${item.frame_number} ${item.order_number}`.toLowerCase()
      const searchText = queryText.toLowerCase()
      return text.includes(searchText)
    },
    filterUsers (item, queryText, itemText) {
      const text = `${item.name} ${item.lastname} ${item.email}`.toLowerCase()
      const searchText = queryText.toLowerCase()
      return text.includes(searchText)
    },
    filterDrivers (item, queryText, itemText) {
      const text = `${item.name} ${item.document_number}`.toLowerCase()
      const searchText = queryText.toLowerCase()
      return text.includes(searchText)
    },
    displayText (item) {
      return item.document_number ? `${item.name} ${item.lastname} - ${item.document_number}` : `${item.name} ${item.lastname}`
    },
    displayTextUser (item) {
      return `${item.name} ${item.lastname} - ${item.email}`
    },
    handleImageChange (files) {
      if (!files) {
        this.fields.damage_images = []
        this.imagePreviewUrls = []
        return
      }
      // Limpiar URLs previas
      this.imagePreviewUrls.forEach(url => URL.revokeObjectURL(url))
      this.imagePreviewUrls = []
      // Filtrar solo las imágenes que podemos agregar (hasta 4 en total)
      const availableSlots = Math.max(0, 4 - this.savedImagesCount)
      const filesToAdd = Array.from(files).slice(0, availableSlots)
      // Actualizar el array de imágenes
      this.fields.damage_images = filesToAdd
      this.fields._images = filesToAdd
      // Generar URLs de vista previa
      filesToAdd.forEach(file => {
        this.imagePreviewUrls.push(URL.createObjectURL(file))
      })
    },
    removeSavedImage (index) {
    // Si la imagen tiene id, agregarla a la lista de imágenes a eliminar
      if (this.fields.imagesUrl[index].id) {
        this.imagesToRemove.push(this.fields.imagesUrl[index].id)
      }
      // Eliminar la imagen de la vista
      this.fields.imagesUrl.splice(index, 1)
    },
    removeNewImage (index) {
      // Eliminar la vista previa
      URL.revokeObjectURL(this.imagePreviewUrls[index])
      this.imagePreviewUrls.splice(index, 1)
      // Eliminar el archivo correspondiente
      const updatedFiles = [...this.fields.damage_images]
      updatedFiles.splice(index, 1)
      this.fields.damage_images = updatedFiles
      this.fields._images = updatedFiles
    },
    handleFileChange (event) {
      const files = event.target.files
      let formData = new FormData()
      formData.append('image', files[0])
      this.$store.dispatch('SCAN_PLATE_NUMBER', formData).then(response => {
      }).catch(error => {
        console.error('Error scanning plate number:', error)
      })
    },

    // Métodos para el estado de matrícula
    getMatriculaColor(value) {
      const colors = {
        1: 'success',
        2: 'warning',
        0: 'error'
      }
      return colors[value] || 'primary'
    },

    getMatriculaIcon(value) {
      const icons = {
        1: 'mdi-check-circle',
        2: 'mdi-alert',
        0: 'mdi-alert-circle'
      }
      return icons[value] || 'mdi-help-circle'
    },
    scanPlateNumber () {
      this.$refs.fileInput.click()
    },
    async tieneMovimiento () {
      if (this.fields.vehicle_id) {
        const { data } = await MovimientoService.get({ 'filter[vehicle_id]': this.fields.vehicle_id })
        return data.data.length > 0 // Devuelve true si hay movimientos, false si no hay
      }
      return false // Si no hay vehicle_id, asumimos que no tiene movimientos
    },
    getTransportIcon (transport) {
      const icons = {
        'DRIVER': 'mdi-account-hard-hat',
        'GRUA': 'mdi-tow-truck',
        'YO MISMO': 'mdi-account',
        'CLIENTE': 'mdi-account-tie'
      }
      return icons[transport] || 'mdi-truck'
    },    
    getTransportColor (transport) {
      const colors = {
        'DRIVER': 'blue',
        'GRUA': 'orange',
        'YO MISMO': 'purple',
        'CLIENTE': 'green'
      }
      return colors[transport] || 'primary'
    },

    openImageDialog (imageUrl) {
      this.selectedImageUrl = imageUrl
      this.imageDialog = true
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'CURRENT_USER'
    }),
    isNewRecord () {
      return !this.value
    },
    title () {
      return this.isNewRecord ? 'Nuevo Movimiento' : 'Modificar Movimiento'
    },
    saveIsDisabled () {
      return this.loading || !this.validations.isValid
    },
    vehicleTypeOptions() {
      // Base de las opciones - misma lógica que en MovementProcessingView / TransportForm.vue:463
      const options = [
        { text: 'MATRICULADO', value: 'MATRICULADO', color: 'success', disabled: !this.hasPlateNumber },
        { text: 'PLACAS ROJAS', value: 'PLACAS ROJAS', color: 'warning', disabled: this.hasPlateNumber },
        { text: 'NO MATRICULADO', value: 'NO MATRICULADO', color: 'error', disabled: this.hasPlateNumber }
      ]
      return options
    },
    hasPlateNumber() {
      return !!(this.selectedVehicle?.plate_number || this.value?.vehicle?.plate_number || this.fields?.vehicle?.plate_number)
    },
    displayUserText () {
      return (item) => `${item.name} ${item.lastname} - ${item.email}`
    },
    totalImagesCount () {
      return this.savedImagesCount + this.newImagesCount
    },
    savedImagesCount () {
      return this.fields.imagesUrl ? this.fields.imagesUrl.length : 0
    },
    newImagesCount () {
      return this.imagePreviewUrls.length
    },
    remainingImageSlots () {
      return Math.max(0, 4 - this.totalImagesCount)
    },    
    canAddMoreImages () {
      return this.remainingImageSlots > 0
    },
    displayDriverText () {
      return (item) => `${item.name}`
    },
    isMovementCompleted () {
      return this.value && this.value.status === 'completed'
    }
  },

  watch: {
    'fields.datetime': function (newValue) {
      if (newValue !== null && newValue !== undefined && newValue !== this.fields.delivery_date) {
        this.fields.delivery_date = newValue
      }
    },
    'fields.delivery_date': function (newValue) {
      if (newValue !== null && newValue !== undefined && newValue !== this.fields.datetime) {
        this.fields.datetime = newValue
      }
    },
    'vehiculoState.search': function (newSearch) {
      if (newSearch === '') {
        this.vehiculoDebouncedSearch()
      }
    },
    selectedVehicle: async function (newVehicle) {
      this.fields.vehicle_id = newVehicle ? newVehicle.id : null
      if (newVehicle) {
        this.tieneMovimientos = await this.tieneMovimiento() // Actualiza la variable reactiva
      } else {
        this.tieneMovimientos = false // Si no hay vehículo seleccionado, no hay movimientos
      }
    },
    selectedUser: function (newUser) {
      this.fields.responsible_id = newUser ? newUser.id : null
    },
    selectedDriver: function (newDriver) {
      this.fields.driver_id = newDriver ? newDriver.id : null
    },
    // Auto-selección de estado de matrícula - misma lógica que MovementProcessingView.vue:1520 y TransportForm
    vehicleTypeOptions: {
      handler(newOptions) {
        if (newOptions && newOptions.length > 0 && !this.driverType) {
          const enabledOptions = newOptions.filter(opt => !opt.disabled)
          // Si el vehículo está matriculado (tiene plate_number), pre-seleccionar "MATRICULADO"
          if (this.hasPlateNumber) {
            const matriculadoOption = enabledOptions.find(opt => opt.value === 'MATRICULADO')
            if (matriculadoOption) {
              this.driverType = matriculadoOption.value
              return
            }
          }
          // Si solo hay una opción habilitada, seleccionarla automáticamente
          if (enabledOptions.length === 1) {
            this.driverType = enabledOptions[0].value
          }
        }
      },
      immediate: true
    },
    hasPlateNumber: {
      handler() {
        if (!this.driverType && this.vehicleTypeOptions && this.vehicleTypeOptions.length > 0) {
          const enabledOptions = this.vehicleTypeOptions.filter(opt => !opt.disabled)
          if (this.hasPlateNumber) {
            const matriculadoOption = enabledOptions.find(opt => opt.value === 'MATRICULADO')
            if (matriculadoOption) {
              this.driverType = matriculadoOption.value
            }
          }
        }
      },
      immediate: true
    }
  }
}
</script>
<style scoped>
.movement-form-card {
  border-radius: 8px;
  overflow: hidden;
}

.vehicle-info-header {
  background-color: #f5f5f5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  transition: background-color 0.3s ease;
}

.vehicle-info-header:hover {
  background-color: #eef5ff;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.87);
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.section-title .v-icon {
  margin-right: 8px;
}

/* Mejoras para las tarjetas de equipamiento */
.equipment-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  transition: box-shadow 0.3s ease, transform 0.2s ease;
  height: 100%;
}

.equipment-card:hover {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* Mejoras para las pestañas */
::v-deep .v-tabs-slider {
  height: 3px;
}

::v-deep .v-tab {
  letter-spacing: 0.5px;
  text-transform: none;
  font-weight: 500;
}

::v-deep .v-tab--active {
  font-weight: 600;
}

/* Efecto hover para elementos seleccionables */
::v-deep .v-select__selection,
::v-deep .v-autocomplete__selection {
  font-weight: 500;
}

/* Mejorar espaciado en dispositivos móviles */
@media (max-width: 600px) {
  .pa-6 {
    padding: 16px !important;
  }
  .vehicle-info-header {
    padding: 12px !important;
  }
}

/* Animación para las transiciones */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition-duration: 0.3s;
}
.image-preview-card, .new-image-preview-card {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.image-preview {
  transition: transform 0.3s ease;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview-card:hover .image-overlay,
.new-image-preview-card:hover .image-overlay {
  opacity: 1;
}

.image-preview-card:hover .image-preview,
.new-image-preview-card:hover .image-preview {
  transform: scale(1.05);
}

.image-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
}

.image-dialog-content {
  max-height: 80vh;
  background-color: #000;
}

.saved-images-container, .new-images-container {
  transition: all 0.3s ease;
}
</style>
